/**
 * Route guards.
 *
 * Three gates, matching the product rules: every route under `/me` needs a
 * session, only creating an event needs an invitation (administrators are exempt
 * because they can already do everything), and the console needs one concrete
 * capability. All of them keep the intended destination in `?redirect=` so
 * signing in returns the user to where they were.
 *
 * The session cookie is invisible to JavaScript, so "no session" can only be
 * decided after the server answered: until then the guard renders the loading
 * block instead of bouncing a signed-in user to the sign-in page.
 */

import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { AdminCapability } from '@shared/constants'

import { useCurrentUser, useMe } from '../../hooks/use-auth'
import { loginRedirectPath } from '../../lib/login-redirect'
import { ErrorBlock, LoadingBlock } from './states'

interface GuardProps {
  children: ReactNode
}

/** Waiting state shared by the guards while the session is unknown. */
function GuardLoading() {
  const { t } = useTranslation()

  return (
    <div className="container-narrow">
      <LoadingBlock label={t('common.loading')} />
    </div>
  )
}

/**
 * Failure state shared by the guards when the session could not be loaded.
 *
 * Without it a failed `/api/auth/me` would leave the guard waiting forever,
 * because "signed out" is only ever concluded from a server answer.
 */
function GuardError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation()

  return (
    <div className="container-narrow">
      <ErrorBlock title={t('error.internal_error')} onRetry={onRetry} retryLabel={t('common.retry')} />
    </div>
  )
}

/**
 * What the signed-in gate decided about the visitor.
 *
 * The branches are separated so a page that lives inside its own container can
 * render them itself: `RequireAuth` owns the route-level container, while a page
 * under the share frame already sits inside one and must not nest another.
 */
export type AuthGuard =
  | { status: 'allowed' }
  | { status: 'loading' }
  | { status: 'failed'; retry: () => void }
  | { status: 'anonymous'; to: string }

/** Decides whether the signed-in gate lets the current visitor through. */
export function useAuthGuard(): AuthGuard {
  const location = useLocation()
  const { isAuthenticated, hydrating } = useCurrentUser()
  const me = useMe()

  if (me.isError) {
    return { status: 'failed', retry: () => void me.refetch() }
  }

  if (!isAuthenticated) {
    if (hydrating || me.isPending) {
      return { status: 'loading' }
    }
    return { status: 'anonymous', to: loginRedirectPath(location.pathname, location.search) }
  }

  return { status: 'allowed' }
}

/**
 * Renders whatever the signed-in gate decided, or `children` once it allowed them.
 *
 * `children` is optional so a page can use this as an early return for the
 * waiting, failure and redirect branches while keeping its own body untouched.
 */
export function AuthGate({ guard, children }: { guard: AuthGuard; children?: ReactNode }) {
  if (guard.status === 'failed') {
    return <GuardError onRetry={guard.retry} />
  }

  if (guard.status === 'loading') {
    return <GuardLoading />
  }

  if (guard.status === 'anonymous') {
    return <Navigate to={guard.to} replace />
  }

  return <>{children}</>
}

/** Requires a signed-in account. */
export function RequireAuth({ children }: GuardProps) {
  return <AuthGate guard={useAuthGuard()}>{children}</AuthGate>
}

/**
 * Requires the invitation that unlocks event creation.
 *
 * Administrators pass without an invite code: they already hold every capability
 * the invitation would grant. The check happens when the user acts, not when the
 * page loads, so an uninvited visitor can still browse everything else.
 */
export function RequireInvite({ children }: GuardProps) {
  const { isInvited, isAdmin } = useCurrentUser()
  const me = useMe()
  const allowed = isInvited || isAdmin

  if (me.isError) {
    return <GuardError onRetry={() => void me.refetch()} />
  }

  if (!allowed) {
    if (me.isPending) {
      return <GuardLoading />
    }
    return <Navigate to="/me/invite" replace />
  }

  return <>{children}</>
}

export interface RequireCapabilityProps extends GuardProps {
  /**
   * Capability the caller must hold.
   *
   * Omitting it only requires being an administrator at all, which is the right
   * gate for the console entry point rather than for one of its pages.
   */
  capability?: AdminCapability
}

/**
 * Requires an administrator capability.
 *
 * A missing capability is not an error page: the console is simply not part of
 * this account's navigation, so the visitor lands back on their own area.
 */
export function RequireCapability({ children, capability }: RequireCapabilityProps) {
  const { capabilities } = useCurrentUser()
  const me = useMe()
  const allowed = capability ? capabilities.includes(capability) : capabilities.length > 0

  if (me.isError) {
    return <GuardError onRetry={() => void me.refetch()} />
  }

  if (!allowed) {
    if (me.isPending) {
      return <GuardLoading />
    }
    return <Navigate to="/me" replace />
  }

  return <>{children}</>
}
